import React, { useContext, useMemo, useState } from 'react';
import { Button, MenuItem, Select, Typography, Box, LinearProgress } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useLocation, useNavigate, useParams } from 'react-router';
import { AppContext } from '../../App';
import DialogGlynt, { FormControlStyled, FormLabelStyled } from '../common/Dialog/DialogGlynt';
import DialogTitleGlynt from '../common/Dialog/DialogTitleGlynt';
import DialogContentGlynt from '../common/Dialog/DialogContentGlynt';
import DialogActionsGlynt from '../common/Dialog/DialogActionsGlynt';
import { useCreatePackage } from '../hooks/packages/useCreatePackage';
import useGetPackager from '../hooks/packager/useGetPackager';
import { extractApiError } from '../../utils/extractApiError';

const STREAM = 'STREAM';

export type CreateDraftPackageMode = 'documents' | 'extraction_batches';

interface CreateDraftPackageProps {
  mode?: CreateDraftPackageMode;
}

const COPY: Record<
  CreateDraftPackageMode,
  { entity: string; entityPlural: string; cypressKey: string }
> = {
  documents: {
    entity: 'document',
    entityPlural: 'documents',
    cypressKey: 'manifest',
  },
  extraction_batches: {
    entity: 'extraction batch',
    entityPlural: 'extraction batches',
    cypressKey: 'eb',
  },
};

function DocumentTableCreateDraftPackage({ mode = 'documents' }: CreateDraftPackageProps) {
  const { setToast } = useContext(AppContext);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { dpid } = useParams<{ dpid: string }>();

  const [open, setOpen] = useState(false);
  const [selectedPackagerId, setSelectedPackagerId] = useState<string>('');

  const { search } = useLocation();
  const selectedIds = useMemo(() => {
    return new URLSearchParams(search).get('selectedIds')?.split(',').filter(Boolean) || [];
  }, [search]);

  const { entity, entityPlural, cypressKey } = COPY[mode];
  const entityLabel = selectedIds.length === 1 ? entity : entityPlural;

  const { data: packagerData, isLoading: isLoadingPackagers } = useGetPackager(dpid || '');
  const packagers = packagerData?.results || [];

  // STREAM packagers reject the override path with 400 — hide them as targets.
  const eligiblePackagers = packagers.filter((p) => p.packager_schedule_type !== STREAM);

  const { mutate: createPackage, isPending: isCreating } = useCreatePackage({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['packages'] });
      queryClient.invalidateQueries({ queryKey: ['packager'] });
      setToast({
        variant: 'success',
        open: true,
        message: 'Draft package created.',
      });
      setOpen(false);
      if (dpid) {
        navigate(`/data-pools/${dpid}/packager/`);
      }
    },
    onError: (error: AxiosError) => {
      setToast({
        variant: 'error',
        open: true,
        message: extractApiError(error, 'Failed to create draft package.'),
      });
    },
  });

  const openDialog = () => {
    setSelectedPackagerId(eligiblePackagers.length === 1 ? eligiblePackagers[0].id || '' : '');
    setOpen(true);
  };

  const closeModal = () => {
    setOpen(false);
    setSelectedPackagerId('');
  };

  const handleSubmit = () => {
    if (!selectedPackagerId || selectedIds.length === 0) return;
    createPackage({
      packagerId: selectedPackagerId,
      ...(mode === 'documents'
        ? { document_ids: selectedIds }
        : { extraction_batch_ids: selectedIds }),
    });
  };

  const canSubmit = !!selectedPackagerId && selectedIds.length > 0 && !isCreating;

  return (
    <>
      <Button
        data-cy={`btn-${cypressKey}-create-draft-package`}
        disabled={selectedIds.length === 0}
        onClick={openDialog}
        sx={{
          height: 32,
          px: 1.5,
          borderRadius: '6px',
          border: '1px solid #1D9AAA',
          bgcolor: '#fff',
          fontSize: '12.5px',
          fontWeight: 500,
          color: '#1D9AAA',
          textTransform: 'none',
          whiteSpace: 'nowrap',
          '&:hover': { borderColor: '#178a99', bgcolor: 'rgba(29,154,170,0.04)' },
          '&.Mui-disabled': { borderColor: '#BDC3D2', color: '#B0B0B0', bgcolor: '#fff' },
        }}
      >
        Create Draft Package
      </Button>

      <DialogGlynt
        open={open}
        closeModal={closeModal}
        width="450px"
        cypressId="dialog-create-draft-package"
      >
        <DialogTitleGlynt title="Create Draft Package" closeModal={closeModal} />
        <DialogContentGlynt>
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Create a draft package from {selectedIds.length} selected {entityLabel}. The package
              terminates at DRAFT regardless of the packager's delivery schedule.
            </Typography>
          </Box>

          {isLoadingPackagers ? (
            <LinearProgress />
          ) : eligiblePackagers.length === 0 ? (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              No eligible packagers in this data pool. STREAM packagers cannot accept ad-hoc draft
              runs.
            </Typography>
          ) : eligiblePackagers.length > 1 ? (
            <FormControlStyled fullWidth>
              <FormLabelStyled>PACKAGER</FormLabelStyled>
              <Select
                data-cy="select-draft-package-packager"
                value={selectedPackagerId}
                onChange={(e) => setSelectedPackagerId(e.target.value)}
                variant="outlined"
                size="small"
              >
                {eligiblePackagers.map((p) => (
                  <MenuItem key={p.id} value={p.id || ''}>
                    {p.label || p.id}
                  </MenuItem>
                ))}
              </Select>
            </FormControlStyled>
          ) : null}
        </DialogContentGlynt>
        <DialogActionsGlynt
          handleSubmit={handleSubmit}
          closeModal={closeModal}
          cancelCypressId="btn-create-draft-package-cancel"
          submitCypressId="btn-create-draft-package-submit"
          clearCypressId="btn-create-draft-package-clear"
          submitButtonText="Create"
          submitLoading={isCreating}
          showCancelButton={true}
          showClearButton={false}
          submitDisabled={!canSubmit}
        />
      </DialogGlynt>
    </>
  );
}

export default DocumentTableCreateDraftPackage;
